import { unwrap, type ApiClient } from "@majourneys/api-client";
import { activityHrefParts } from "./activity-href";
import type { MentionTarget } from "./linkify-mentions";

export interface CityMentionTarget extends MentionTarget {
  slug: string;
}

interface CityCatalogItem {
  slug: string;
  title: string;
  mode?: "bookable" | "explore";
  segment?: string | null;
  category: { slug: string } | null;
  city: { slug: string } | null;
}

/**
 * Every activity/explore item in a city as a linkable mention target.
 * Shared between the show page (useQuery) and route loaders
 * (ensureQueryData) so the venue-name links inside editorial blocks land
 * in the SSR HTML. Cached per city+lang; consumers filter out the page's
 * own slug.
 */
export function cityMentionTargetsQueryOptions(
  api: ApiClient,
  citySlug: string,
  lang: string,
) {
  return {
    queryKey: ["activities", "city-mention-targets", citySlug, lang] as const,
    queryFn: async (): Promise<CityMentionTarget[]> => {
      const response = unwrap(
        await api.activities.explore.search.get({
          query: { city: citySlug, mode: "all", page_size: "100", lang },
        }),
      );
      const items = (response?.data ?? []) as CityCatalogItem[];
      return items.map((item) => {
        const parts = activityHrefParts({
          lang,
          citySlug: item.city?.slug ?? null,
          categorySlug: item.category?.slug ?? null,
          segment: item.mode === "explore" ? item.segment ?? null : null,
          slug: item.slug,
        });
        return {
          slug: item.slug,
          title: item.title,
          href: `/${parts.params.lang}/${parts.params.city}/${parts.params.category}/${parts.params.slug}`,
        };
      });
    },
    // City catalogs change rarely; avoid refetching on every page view.
    staleTime: 5 * 60 * 1000,
  };
}
